import { CostAssessmentHeaderRow, CostAssessmentRecordRow, CostAssessmentValueRow, DepreciatedRecordRow } from "../../types";
import { useLandValueInput, useSizeAsQuantity } from "../records";
import { RecordOptions } from "./calculation";

export function normalizeRecords(
    records: CostAssessmentRecordRow[],
    recordOptions: RecordOptions
): DepreciatedRecordRow[] {
    const {
        useLandValueInput: _useLandValueInput,
        useSizeAsQuantity: _useSizeAsQuantity,
        landInput,
        level,
        size,
        unitAreaMeasurementId
    } = recordOptions

    let _records = [...records]
    if (_useLandValueInput && landInput) {
        _records = useLandValueInput(_records, level, landInput)
    }
    if (_useSizeAsQuantity) {
        _records = useSizeAsQuantity(
            _records, 
            size, 
            unitAreaMeasurementId,
            {
                isTrue: _useLandValueInput,
                value: landInput?.size
            }
        )
    }
    const mappedRecords = calculateGroups(
        _records,
        size
    )
    return mappedRecords.map(item => {
        if (item.kind === 'header') {
            return {
                kind: 'header',
                background_color: item.backgroundColor,
                category: item.category,
                code: item.code,
                is_open: item.isOpen,
                is_hidden: item.isHidden,
                total_cost: item.totalCost,
                per_unit: item.costPerUnit,
                depreciated_cost: 0,
                depreciated_per_unit: 0,
                depreciation: item.depreciation
            }
        }
        return {
            kind: 'value',
            background_color: item.backgroundColor,
            category: item.category,
            code: item.code,
            is_hidden: item.isHidden,
            useful_life: item.useful_life,
            quantity: item.quantity,
            unit: item.unit,
            tooltips: item.tooltips,
            is_removed: item.isRemoved,
            total_cost: item.totalCost,
            unit_rate: item.unit_rate,
            per_unit: item.costPerUnit,
            depreciated_cost: 0,
            depreciated_per_unit: 0,
            depreciation: item.depreciation
        }
    })
}

// --- Augmented Types for Internal Calculation ---

// Extends the value row to include its calculated total cost.
interface CalculatedValueRow extends CostAssessmentValueRow {
    totalCost: number;
}

// The internal tree node structure, now holding calculated totals.
type CalculatedTreeNode = {
    header?: CostAssessmentHeaderRow;
    values: CalculatedValueRow[];
    children: Record<string, CalculatedTreeNode>;
    totalCost: number;       // Will be calculated for the group.
    costPerUnit: number; // Will be calculated for the group.
};
type CalculatedTree = Record<string, CalculatedTreeNode>;

// The final output row type will have cost properties on all rows.
type OutputRow = (CostAssessmentHeaderRow | CostAssessmentValueRow) & {
    totalCost: number;
    costPerUnit: number;
};


// --- Main Exported Function ---

/**
 * Calculates costs for all rows, including roll-up totals for headers.
 */
export function calculateGroups(
    rows: CostAssessmentRecordRow[],
    propertySize: number
): OutputRow[] {
    const level = determineLevel(rows);

    if (level < 3) {
        // Simple case remains the same, but we add the properties.
        const result: OutputRow[] = [];
        for (const row of rows) {
            if (row.kind === 'header') {
                // Headers in simple levels have no children to sum.
                result.push({ ...row, totalCost: 0, costPerUnit: 0 });
                continue;
            }
            const costs = calculateTotalCostAndCostPerUnit(row.unit_rate, row.quantity, propertySize);
            result.push({ ...row, ...costs });
        }
        return result;
    }

    // Multi-stage calculation for complex levels:
    const tree = buildCodeTree(rows);

    // 1. Calculate initial totalCost for all value rows.
    precomputeInitialCosts(tree);

    // 2. Apply special logic, which now uses and updates totalCost.
    applyUnit17FromBaseline(tree, '1.01');
    applyUnits13to16FromRoot(tree);

    // 3. Roll up totals for all headers.
    rollupHeaderTotals(tree, propertySize);

    // 4. Sort and flatten the fully calculated tree.
    const sortedTree = sortTree(tree);
    return flattenTree(sortedTree, propertySize);
}


// --- Core Calculation Logic ---

/**
 * Calculates total cost and cost per unit from base values.
 */
function calculateTotalCostAndCostPerUnit(unitRate: number, quantity: number, propertySize: number): {
    totalCost: number;
    costPerUnit: number;
} {
    const totalCost = unitRate * quantity;
    const costPerUnit = propertySize > 0 ? totalCost / propertySize : 0;
    return { totalCost, costPerUnit };
}

/**
 * STAGE 1: Pre-computes initial totalCost for every value row in the tree.
 */
function precomputeInitialCosts(tree: CalculatedTree): void {
    const stack = Object.values(tree);
    while (stack.length > 0) {
        const node = stack.pop()!;
        for (let i = 0; i < node.values.length; i++) {
            const item = node.values[i];
            // Replace the item with a new object that includes its initial totalCost.
            node.values[i] = { ...item, totalCost: item.unit_rate * item.quantity };
        }
        for (const key in node.children) {
            stack.push(node.children[key]);
        }
    }
}

/**
 * STAGE 2 (Helper): Sums the pre-calculated `totalCost` of descendants.
 */
function sumDescendantTotalsFiltered(
    node: CalculatedTreeNode,
    excludedUnits: Set<number>,
    excludedPrefixes: string[]
): number {
    let total = 0;
    const stack: CalculatedTreeNode[] = [node];
    while (stack.length > 0) {
        const currentNode = stack.pop()!;
        for (const item of currentNode.values) {
            if (excludedUnits.has(item.unit) || startsWithAny(item.code, excludedPrefixes)) {
                continue;
            }
            if (item.isRemoved) {
                continue;
            }
            // Now sums the pre-calculated totalCost property.
            total += item.totalCost;
        }
        const childKeys = Object.keys(currentNode.children);
        for (const key of childKeys) {
            if (!startsWithAny(key, excludedPrefixes)) {
                stack.push(currentNode.children[key]);
            }
        }
    }
    return total;
}

/**
 * STAGE 2: Applies unit 16 logic. MUTATES the tree.
 */
function applyUnit17FromBaseline(tree: CalculatedTree, baselineCode: string = '1.01'): void {
    const baselineNode = findNode(tree, baselineCode);
    if (!baselineNode) return;

    const baselineTotal = sumDescendantTotalsFiltered(baselineNode, new Set(), []);
    if (baselineTotal === 0) return;

    const stack: CalculatedTreeNode[] = Object.values(tree);
    while (stack.length > 0) {
        const node = stack.pop()!;
        for (let i = 0; i < node.values.length; i++) {
            const item = node.values[i];
            if (item.unit === 17 && item.quantity > 0) {
                const factor = item.quantity / 100;
                const newTotalCost = baselineTotal * factor;
                const newUnitRate = newTotalCost;
                // Update both unit_rate and totalCost for the item.
                node.values[i] = { ...item, unit_rate: newUnitRate, totalCost: newTotalCost };
            }
        }
        for (const key in node.children) {
            stack.push(node.children[key]);
        }
    }
}

/**
 * STAGE 2: Applies units 12-15 logic. MUTATES the tree.
 * It now calculates two separate baselines: one for totalCost and one for unit_rate.
 */
function applyUnits13to16FromRoot(
    tree: CalculatedTree,
    opts?: { rootCode?: string; perUnitExclusions?: Record<number, string[]> }
): void {
    const rootCode = opts?.rootCode ?? '2';
    const perUnitExclusions = opts?.perUnitExclusions ?? {
        13: ['2.08', '2.10'],
        14: ['2.08', '2.09'],
        15: ['2.09', '2.10'],
        16: ['2.13'],
    };
    const targetUnits = new Set<number>([13, 14, 15, 16]);
    const baselineRoot = tree[rootCode];
    if (!baselineRoot) return;

    // --- MODIFICATION START ---
    // Create objects to hold both types of baselines.
    const baselineTotalCostByUnit: Record<number, number> = {};
    const baselineUnitRateByUnit: Record<number, number> = {};

    const unitsForBaseline = [13, 14, 15, 16];
    for (const u of unitsForBaseline) {
        const exclusions = perUnitExclusions[u] ?? [];
        // Calculate the baseline for totalCost
        baselineTotalCostByUnit[u] = sumDescendantTotalsFiltered(baselineRoot, targetUnits, exclusions);
        // Calculate the baseline for unitRate
        baselineUnitRateByUnit[u] = sumDescendantUnitRatesFiltered(baselineRoot, targetUnits, exclusions);
    }

    const stack: CalculatedTreeNode[] = Object.values(tree);
    while (stack.length > 0) {
        const node = stack.pop()!;
        for (let i = 0; i < node.values.length; i++) {
            const item = node.values[i];
            if (item.unit && targetUnits.has(item.unit)) {
                // Get the appropriate baseline for each calculation
                const baselineTotalCost = baselineTotalCostByUnit[item.unit] || 0;
                const baselineUnitRate = baselineUnitRateByUnit[item.unit] || 0;

                if (!item.quantity) continue;
                const factor = item.quantity / 100;

                // Calculate new values independently from their respective baselines
                const newTotalCost = baselineTotalCost * factor;
                const newUnitRate = baselineUnitRate * factor;

                // Update the item with both newly calculated, independent values
                node.values[i] = { ...item, unit_rate: newUnitRate, totalCost: newTotalCost };
            }
        }
        for (const key in node.children) {
            stack.push(node.children[key]);
        }
    }
    // --- MODIFICATION END ---
}

/**
 * STAGE 3: Recursively calculates and sets totalCost for each header node.
 */
function rollupHeaderTotals(tree: CalculatedTree, propertySize: number): void {
    const postOrderTraversal = (node: CalculatedTreeNode): number => {
        let childrenTotal = 0;
        for (const key in node.children) {
            childrenTotal += postOrderTraversal(node.children[key]);
        }
        const valuesTotal = node.values.reduce((sum, v) => sum + v.totalCost, 0);
        const nodeTotal = childrenTotal + valuesTotal;

        // Mutate the node to set its final calculated totals.
        node.totalCost = nodeTotal;
        node.costPerUnit = propertySize > 0 ? nodeTotal / propertySize : 0;

        return nodeTotal;
    };

    // Start the roll-up from the root of the tree.
    for (const key in tree) {
        postOrderTraversal(tree[key]);
    }
}


// --- Tree Construction and Manipulation Helpers ---

function findOrCreateNode(tree: CalculatedTree, code: string): CalculatedTreeNode {
    const parts = code.split('.');
    let currentChildren = tree;
    let node: CalculatedTreeNode | undefined;
    let currentKey = '';
    for (let i = 0; i < parts.length; i++) {
        currentKey = currentKey ? `${currentKey}.${parts[i]}` : parts[i];
        if (!currentChildren[currentKey]) {
            // Initialize new nodes with zeroed cost properties.
            currentChildren[currentKey] = { values: [], children: {}, totalCost: 0, costPerUnit: 0 };
        }
        node = currentChildren[currentKey];
        currentChildren = node.children;
    }
    return node!;
}

export function buildCodeTree(items: CostAssessmentRecordRow[]): CalculatedTree {
    const tree: CalculatedTree = {};
    for (const item of items) {
        if (item.kind === 'header') {
            const node = findOrCreateNode(tree, item.code);
            node.header = item;
        } else {
            const parentCode = item.code.split('.').slice(0, -1).join('.');
            if (parentCode) {
                const parentNode = findOrCreateNode(tree, parentCode);
                // Temporarily cast to CalculatedValueRow during the build process.
                parentNode.values.push({ ...item, totalCost: 0 });
            }
        }
    }
    return tree;
}

/**
 * STAGE 4: Flattens the calculated tree into a sorted array, including all cost properties.
 */
const flattenTree = (tree: CalculatedTree, propertySize: number): OutputRow[] => {
    const result: OutputRow[] = [];
    const walk = (t: CalculatedTree) => {
        const keys = Object.keys(t).sort(dotNumCompare);
        for (const key of keys) {
            const node = t[key];
            if (node.header) {
                // Add the header with its final rolled-up costs.
                result.push({ ...node.header, totalCost: node.totalCost, costPerUnit: node.costPerUnit });
            }
            const sortedValues = [...node.values].sort((a, b) => dotNumCompare(a.code, b.code));
            for (const val of sortedValues) {
                // Add the value item with its final cost and per-unit cost.
                const costPerUnit = propertySize > 0 ? val.totalCost / propertySize : 0;
                result.push({ ...val, costPerUnit });
            }
            walk(node.children);
        }
    };
    walk(tree);
    return result;
};

const sortTree = (tree: CalculatedTree): CalculatedTree => {
    const sortedKeys = Object.keys(tree).sort(dotNumCompare);
    const result: CalculatedTree = {};
    for (const k of sortedKeys) {
        const node = tree[k];
        result[k] = { ...node, children: sortTree(node.children) };
    }
    return result;
};

const findNode = (tree: CalculatedTree, code: string): CalculatedTreeNode | undefined => {
    if (tree[code]) return tree[code];
    for (const k in tree) {
        const node = tree[k]
        if (node.values.some(value => value.code === code)) {
            return node;
        }
        const found = findNode(node.children, code);
        if (found) return found;
    }
    return undefined;
};

// --- Unchanged Utility Helpers ---
function determineLevel(rows: CostAssessmentRecordRow[]) {
    if (rows.length <= 1) return 1; if (rows.length === 2) return 2; if (rows.length === 17) return 3; return 4;
}
const dotNumCompare = (a: string, b: string) => {
    const A = a.split('.').map(Number); const B = b.split('.').map(Number);
    for (let i = 0; i < Math.max(A.length, B.length); i++) {
        const x = A[i] ?? -Infinity; const y = B[i] ?? -Infinity; if (x !== y) return x - y;
    } return 0;
};
const startsWithAny = (code: string, prefixes: string[]) => {
    for (let i = 0; i < prefixes.length; i++) {
        if (code.startsWith(prefixes[i])) return true;
    } return false;
};

/**
 * STAGE 2 (Helper): Sums the pre-calculated `unit_rate` of descendants.
 * This is used to create the baseline for percentage-based unit rates.
 */
function sumDescendantUnitRatesFiltered(
    node: CalculatedTreeNode,
    excludedUnits: Set<number>,
    excludedPrefixes: string[]
): number {
    let total = 0;
    const stack: CalculatedTreeNode[] = [node];
    while (stack.length > 0) {
        const currentNode = stack.pop()!;
        for (const item of currentNode.values) {
            if (excludedUnits.has(item.unit) || startsWithAny(item.code, excludedPrefixes)) {
                continue;
            }
            if (item.isRemoved) {
                continue;
            }
            // Sums the unit_rate property, handling potential null values.
            total += (item.unit_rate || 0);
        }
        const childKeys = Object.keys(currentNode.children);
        for (const key of childKeys) {
            if (!startsWithAny(key, excludedPrefixes)) {
                stack.push(currentNode.children[key]);
            }
        }
    }
    return total;
}